import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Inbox, PhoneCall, Archive, Trash2, RotateCcw, Mail, Phone, Calendar, Building2, Filter } from "lucide-react";
import { useI18n } from "@/i18n";
import api from "@/api";

const STATUSES = ["all", "new", "contacted", "archived"];

export default function AdminDashboard() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, archived: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([
        api.get("/admin/quotes"),
        api.get("/admin/stats"),
      ]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("gsm_token");
        navigate("/admin", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const logout = () => {
    localStorage.removeItem("gsm_token");
    localStorage.removeItem("gsm_user");
    navigate("/admin", { replace: true });
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/quotes/${id}`, { status });
    await load();
    if (selected?.id === id) setSelected((s) => ({ ...s, status }));
  };

  const remove = async (id) => {
    if (!window.confirm(t("admin.confirm.delete"))) return;
    await api.delete(`/admin/quotes/${id}`);
    if (selected?.id === id) setSelected(null);
    await load();
  };

  const filtered = useMemo(() => {
    return filter === "all" ? quotes : quotes.filter((q) => q.status === filter);
  }, [quotes, filter]);

  const dateFmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", {
        year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div data-testid="page-admin-dashboard" className="min-h-[calc(100vh-5rem)] bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500 font-mono">GSM · Admin</div>
            <h1 className="mt-2 font-serif text-4xl md:text-5xl tracking-tight text-stone-900">{t("admin.dashboard.title")}</h1>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm text-stone-700 hover:text-[#B95736] transition-colors">
            <LogOut size={16} /> {t("admin.dashboard.logout")}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatBox testid="stat-total" icon={Inbox} label="Total" value={stats.total} accent="#1C1917" />
          <StatBox testid="stat-new" icon={Inbox} label={t("admin.status.new")} value={stats.new} accent="#B95736" />
          <StatBox testid="stat-contacted" icon={PhoneCall} label={t("admin.status.contacted")} value={stats.contacted} accent="#293845" />
          <StatBox testid="stat-archived" icon={Archive} label={t("admin.status.archived")} value={stats.archived} accent="#78716C" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <Filter size={14} className="text-stone-500" />
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`admin-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-mono tracking-widest uppercase transition-colors border ${
                filter === s ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:border-stone-900"
              }`}
            >
              {t(`admin.dashboard.filter.${s}`)}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* List */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
              {loading ? (
                <div className="p-8 text-stone-500 text-sm">…</div>
              ) : filtered.length === 0 ? (
                <div data-testid="admin-empty" className="p-10 text-center text-stone-500">{t("admin.dashboard.empty")}</div>
              ) : (
                <ul className="divide-y divide-stone-200" data-testid="admin-quote-list">
                  {filtered.map((q) => (
                    <li key={q.id}>
                      <button
                        data-testid={`admin-quote-${q.id}`}
                        onClick={() => setSelected(q)}
                        className={`w-full text-left px-5 py-4 hover:bg-stone-50 transition-colors flex items-start gap-4 ${selected?.id === q.id ? "bg-stone-50" : ""}`}
                      >
                        <StatusBadge status={q.status} t={t} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3">
                            <div className="font-medium text-stone-900 truncate">{q.name}</div>
                            <div className="text-xs text-stone-500 font-mono flex-shrink-0">{dateFmt(q.created_at)}</div>
                          </div>
                          <div className="text-sm text-stone-600 truncate">{q.service} — {q.email}</div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-5">
            <div className="bg-white border border-stone-200 rounded-lg p-6 sticky top-28" data-testid="admin-detail-panel">
              {!selected ? (
                <div className="text-stone-500 text-sm py-16 text-center">
                  {lang === "fr" ? "Sélectionnez une soumission." : "Select a submission."}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500 font-mono">{selected.service}</div>
                      <h3 className="mt-1 font-serif text-2xl text-stone-900">{selected.name}</h3>
                    </div>
                    <StatusBadge status={selected.status} t={t} />
                  </div>
                  <ul className="space-y-2 text-sm">
                    <DetailRow icon={Mail} label={selected.email} href={`mailto:${selected.email}`} />
                    <DetailRow icon={Phone} label={selected.phone} href={`tel:${selected.phone}`} />
                    {selected.company && <DetailRow icon={Building2} label={selected.company} />}
                    {selected.preferred_date && <DetailRow icon={Calendar} label={selected.preferred_date} />}
                    <DetailRow icon={Calendar} label={dateFmt(selected.created_at)} muted />
                  </ul>
                  <div className="pt-2 border-t border-stone-200">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500 font-mono mb-2">Message</div>
                    <p className="text-stone-800 text-sm whitespace-pre-wrap leading-relaxed">{selected.message}</p>
                  </div>

                  <div className="pt-4 flex flex-wrap gap-2">
                    {selected.status !== "contacted" && (
                      <button
                        data-testid="admin-action-mark-contacted"
                        onClick={() => updateStatus(selected.id, "contacted")}
                        className="btn-primary text-xs"
                      >
                        <PhoneCall size={14} /> {t("admin.action.mark_contacted")}
                      </button>
                    )}
                    {selected.status !== "archived" ? (
                      <button
                        data-testid="admin-action-archive"
                        onClick={() => updateStatus(selected.id, "archived")}
                        className="btn-outline text-xs"
                      >
                        <Archive size={14} /> {t("admin.action.archive")}
                      </button>
                    ) : (
                      <button
                        data-testid="admin-action-reopen"
                        onClick={() => updateStatus(selected.id, "new")}
                        className="btn-outline text-xs"
                      >
                        <RotateCcw size={14} /> {t("admin.action.reopen")}
                      </button>
                    )}
                    <button
                      data-testid="admin-action-delete"
                      onClick={() => remove(selected.id)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs text-red-700 hover:bg-red-50 border border-red-200 transition-colors"
                    >
                      <Trash2 size={14} /> {t("admin.action.delete")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-stone-200 rounded-lg p-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${accent}15`, color: accent }}>
          <Icon size={16} strokeWidth={1.5} />
        </div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-stone-500 font-mono">{label}</div>
      </div>
      <div className="mt-4 font-serif text-4xl text-stone-900">{value}</div>
    </div>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    new: { bg: "bg-[#B95736]/10", text: "text-[#B95736]" },
    contacted: { bg: "bg-emerald-100", text: "text-emerald-800" },
    archived: { bg: "bg-stone-200", text: "text-stone-700" },
  };
  const s = map[status] || map.new;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ${s.bg} ${s.text}`}>
      {t(`admin.status.${status}`)}
    </span>
  );
}

function DetailRow({ icon: Icon, label, href, muted }) {
  const content = (
    <span className={`inline-flex items-center gap-2 ${muted ? "text-stone-500" : "text-stone-800"}`}>
      <Icon size={14} className="flex-shrink-0" />
      <span className="break-all">{label}</span>
    </span>
  );
  return (
    <li>
      {href ? <a href={href} className="hover:text-[#B95736] transition-colors">{content}</a> : content}
    </li>
  );
}
